/**
 * V2 source path: customer/midas/components/v2/MidasPageV2.tsx
 * V1 source: customer/midas/components/MidasPage.tsx (page scaffold)
 * Strategy: scratch-with-reused-handlers
 * Verified no-MUI: yes
 * Phase: 1 + 2A + 2B + 3 + 4.1
 *
 * Phase 4.1 refactor: the V2 sidebar shell (desktop sidebar/rail, tablet
 * rail+drawer, phone takeover) was lifted out of this file into
 * MidasV2Layout.tsx so it can render on both /midas-v2 (chat) and
 * /midas-v2/history (history archive). What remains here is the chat-only
 * page body — top bar, chat area, drag handle, artifact panel, phone artifact
 * takeover. The page is mounted as a child of the layout route, so the
 * sidebar appears to its left (or as a tablet drawer / phone overlay) without
 * any sidebar code in this file.
 *
 * The chat top bar (TopBar component below) is unchanged from the pre-4.1
 * implementation. It is NOT a "MIDAS-branded top bar" replacement — it's the
 * existing chat-page header (Midas wordmark + machine context + "+ Ask Midas"
 * CTA + phone hamburger). It is intentionally NOT shared with the history
 * sub-page, which has its own header.
 *
 * The hamburger button reaches up to the layout via `useOutletContext()` to
 * open the phone sidebar takeover (state lives in MidasV2Layout).
 *
 * Per the Round 11 invariant we wrap the page in a div without `midas-v2-root`
 * here — that class lives on the layout's outermost div now (so shadcn theme
 * variables apply across both sub-pages).
 */
import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Menu, Plus } from "lucide-react";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  setMachineContext,
  startNewChat,
} from "../../../../shared/chatBot/chatBotSlice";
import { setActiveThreadId } from "../../midasPageSlice";
import MidasArtifactPanelV2 from "./MidasArtifactPanelV2";
import MidasChatAreaV2 from "./MidasChatAreaV2";
import MidasDragHandleV2 from "./MidasDragHandleV2";
import MidasPhoneArtifactScreenV2 from "./MidasPhoneArtifactScreenV2";
import { useMidasBreakpoint } from "./hooks/useMidasBreakpoint";
import type { MidasV2OutletContext } from "./MidasV2Layout";

// ════════════════════════════════════════════════════════════════
// usePrefersReducedMotion
// ════════════════════════════════════════════════════════════════

const usePrefersReducedMotion = (): boolean => {
  const [pref, setPref] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => setPref(mql.matches);
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    // Safari < 14 fallback
    if (typeof (mql as any).addListener === "function") {
      (mql as any).addListener(handler);
      return () => (mql as any).removeListener(handler);
    }
    return undefined;
  }, []);
  return pref;
};

// ════════════════════════════════════════════════════════════════
// Top bar
// ════════════════════════════════════════════════════════════════

interface TopBarProps {
  showHamburger: boolean;
  onHamburgerClick: () => void;
  onNewChat: () => void;
}

const TopBar: React.FC<TopBarProps> = ({
  showHamburger,
  onHamburgerClick,
  onNewChat,
}) => {
  const { translate } = useTranslation("chatBot");
  const t = (k: string, f: string) => translate(k as any, { defaultValue: f }) as string;
  const machineContext = useAppSelector((s) => s.chatBot.machineContext);
  const machineName = machineContext?.model
    ? machineContext.manufacturerName
      ? `${machineContext.manufacturerName} ${machineContext.model}`.trim()
      : machineContext.model
    : null;

  return (
    <header className="flex h-[60px] flex-shrink-0 items-center gap-2 border-b border-[rgba(44,38,32,0.08)] bg-midas-bone-light px-3 sm:px-4">
      {showHamburger && (
        <button
          type="button"
          onClick={onHamburgerClick}
          aria-label={t("midas.aria.phoneMenuButton", "Open sidebar")}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md text-midas-graphite outline-none transition-colors duration-150 hover:bg-[rgba(44,38,32,0.06)] focus-visible:ring-2 focus-visible:ring-midas-terracotta motion-reduce:transition-none"
        >
          <Menu className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
        </button>
      )}

      <div className="flex flex-1 items-center justify-center gap-2 truncate">
        {machineName ? (
          <>
            <span
              aria-hidden="true"
              className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#5BBF8B]"
              style={{ boxShadow: "0 0 4px rgba(91,191,139,0.7)" }}
            />
            <span
              className="truncate font-sora text-[14px] font-medium text-midas-graphite"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              {machineName}
              {machineContext?.serialNumber && (
                <span
                  className="ml-2 font-mono text-[11px] text-midas-muted-stone"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  · SN {machineContext.serialNumber}
                </span>
              )}
            </span>
          </>
        ) : (
          <span
            className="font-mono text-[11px] uppercase tracking-[0.16em] text-midas-muted-stone"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            {t("midas.v2.platformModeGeneral", "Platform · General assistant")}
          </span>
        )}
      </div>

      <button
        type="button"
        onClick={onNewChat}
        aria-label={`${t("midas.v2.askMidas", "Ask Midas")} — ${t("midas.v2.newChat", "New chat")}`}
        className="flex h-11 flex-shrink-0 items-center gap-1.5 rounded-md border border-[rgba(44,38,32,0.12)] bg-white px-3 font-sora text-[13px] font-medium text-midas-graphite outline-none transition-colors duration-150 hover:bg-midas-bone-light hover:border-midas-terracotta focus-visible:ring-2 focus-visible:ring-midas-terracotta motion-reduce:transition-none"
        style={{ fontFamily: "'Sora', sans-serif" }}
      >
        <Plus className="h-4 w-4 text-midas-terracotta" strokeWidth={2} aria-hidden="true" />
        <span className="hidden sm:inline">{t("midas.v2.askMidas", "Ask Midas")}</span>
      </button>
    </header>
  );
};

// ════════════════════════════════════════════════════════════════
// MidasPageV2 (chat sub-page body — sidebar provided by MidasV2Layout)
// ════════════════════════════════════════════════════════════════

const MidasPageV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isDesktop, isTablet, isPhone } = useMidasBreakpoint();
  const prefersReducedMotion = usePrefersReducedMotion();

  // Outlet context — supplied by MidasV2Layout. Lets the phone hamburger
  // open the sidebar takeover whose state lives in the layout. Defensive
  // fallback so the page still renders (without phone hamburger) if some
  // future caller mounts it outside the layout.
  const outlet = useOutletContext<MidasV2OutletContext | undefined>();
  const openPhoneSidebar = outlet?.openPhoneSidebar ?? (() => {});

  const artifactPanelOpen = useAppSelector(
    (s) => s.midasPage.artifactPanelOpen
  );
  const hasArtifacts = useAppSelector(
    (s) => s.midasPage.artifactTabs.length > 0
  );

  // Top-bar "Ask Midas" / "+ New chat" — Round 4 decision: lives in TOP BAR, not sidebar
  const handleNewChat = useCallback(() => {
    dispatch(startNewChat());
    dispatch(setActiveThreadId(null));
    dispatch(setMachineContext(null));
  }, [dispatch]);

  // ── Desktop dock visibility (Phase 2 logic preserved verbatim) ──
  const showDesktopDock = isDesktop && (artifactPanelOpen || hasArtifacts);
  const showTabletDock = isTablet && artifactPanelOpen && hasArtifacts;
  const showPhoneTakeover = isPhone && artifactPanelOpen && hasArtifacts;

  return (
    <>
      <TopBar
        showHamburger={isPhone}
        onHamburgerClick={openPhoneSidebar}
        onNewChat={handleNewChat}
      />

      {/* Chat + dock split (Phase 1 + 2). The layout already wraps us in a
          flex column inside the sidebar+page horizontal split, so we just
          flex-1 the chat row inside the remaining vertical space. */}
      <div
        className="flex min-h-0 flex-1"
        style={{ overflow: "hidden" }}
        // Reduced-motion + Safari < 14 are handled per-child where animation
        // matters; keep this wrapper simple.
        aria-hidden={false}
      >
        <MidasChatAreaV2 prefersReducedMotion={prefersReducedMotion} />
        {(showDesktopDock || showTabletDock) && (
          <>
            <MidasDragHandleV2 />
            <MidasArtifactPanelV2 />
          </>
        )}
      </div>

      {/* Phone artifact takeover (Phase 2) */}
      {showPhoneTakeover && <MidasPhoneArtifactScreenV2 />}
    </>
  );
};

export default MidasPageV2;
